import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Box,
    Button,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Container,
    styled,
    Chip,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
    IconButton,
    Card,
    CardContent,
    Divider,
    Grid,
    CircularProgress,
    LinearProgress,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { Add } from '@mui/icons-material';
import UpdateIcon from '@mui/icons-material/Update';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useNavigate, Routes, Route } from 'react-router-dom';

import RegistrarMantenimiento from './RegistrarMantenimiento';

// Componentes estilizados
const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    '&.MuiTableCell-head': {
        backgroundColor: '#19438C',
        color: 'white',
        fontSize: 16,
    },
    '&.MuiTableCell-body': {
        fontSize: 14,
        borderRight: 'none',
        borderLeft: 'none',
    },
}));

const AlertasMantenimiento = ({
    vehiculos,
    setAlertas,
    setHistorial,
    getNombreVehiculo
}) => {
    const navigate = useNavigate();
    
    // Estados para la paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Estados para búsqueda por placa y VIN
    const [placaBusqueda, setPlacaBusqueda] = useState("");
    const [vinBusqueda, setVinBusqueda] = useState("");
    const [placaError, setPlacaError] = useState(false);
    const [vinError, setVinError] = useState(false);
    const [vehiculoSeleccionado, setVehiculoSeleccionado] = useState(null);
    const [isLoadingBusqueda, setIsLoadingBusqueda] = useState(false);
    const [tipoBusqueda, setTipoBusqueda] = useState("placa"); // "placa" o "vin"

    // Estado para alertas próximas
    const [alertasProximas, setAlertasProximas] = useState([]);
    const [isLoadingAlertas, setIsLoadingAlertas] = useState(true);

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    // Estados para formularios
    const initialAlertaState = useMemo(() => ({
        vehiculo_id: '',
        tipo_mantenimiento: 'Preventivo',
        descripcion: '',
        kilometraje_programado: '',
        estado:'pendiente',
        porcentaje_avance:'',
    }), []);

    const [nuevaAlerta, setNuevaAlerta] = useState(initialAlertaState);

    // Estados UI
    const [mostrarFormAlerta, setMostrarFormAlerta] = useState(false);
    const [mostrarFormReprogramar, setMostrarFormReprogramar] = useState(false);
    const [alertaSeleccionada, setAlertaSeleccionada] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nuevoKmProgramado, setNuevoKmProgramado] = useState('');

  // Cargar alertas próximas al montar el componente
useEffect(() => {
    const fetchAlertasProximas = async () => {
        try {
            setIsLoadingAlertas(true);
            const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/alertas/');
            
            if (!response.ok) {
                throw new Error('Error al cargar las alertas próximas');
            }
            
            const data = await response.json();
            setAlertasProximas(data);
            setAlertas(data); // ← Sincronizar ambos estados
        } catch (error) {
            console.error('Error:', error);
            setError(error.message);
        } finally {
            setIsLoadingAlertas(false);
        }
    };
    
    fetchAlertasProximas();
}, []);

    // Manejador de cambios en el tipo de búsqueda
    const handleTipoBusquedaChange = (event, newTipo) => {
        if (newTipo !== null) {
            setTipoBusqueda(newTipo);
            // Limpiar errores y campos al cambiar el tipo de búsqueda
            setPlacaError(false);
            setVinError(false);
            setError(null);
            setPlacaBusqueda("");
            setVinBusqueda("");
        }
    };

    // Función para buscar vehículo por placa o VIN
    const buscarVehiculo = async () => {
        // Validaciones según el tipo de búsqueda
        if (tipoBusqueda === "placa" && !placaBusqueda.trim()) {
            setPlacaError(true);
            setVinError(false);
            return;
        }
        
        if (tipoBusqueda === "vin" && !vinBusqueda.trim()) {
            setVinError(true);
            setPlacaError(false);
            return;
        }

        // Validar que el VIN tenga exactamente 4 dígitos
        if (tipoBusqueda === "vin" && (!/^\d{4}$/.test(vinBusqueda.trim()))) {
            setVinError(true);
            setError("El VIN debe contener exactamente 4 dígitos");
            return;
        }

        setIsLoadingBusqueda(true);
        setError(null);
        setPlacaError(false);
        setVinError(false);
        
        try {
            let url = "";
            if (tipoBusqueda === "placa") {
                url = `https://bei360.injupemp.gob.hn:8444/api/vehiculos/${placaBusqueda.trim()}/`;
            } else {
                url = `https://bei360.injupemp.gob.hn:8444/api/vehiculos/vin/${vinBusqueda.trim()}/`;
            }
            
            const res = await fetch(url);
            
            // Verificar si la respuesta es exitosa
            if (!res.ok) {
                if (res.status === 404) {
                    throw new Error("Vehículo no encontrado");
                } else {
                    throw new Error(`Error del servidor: ${res.status}`);
                }
            }
            
            const data = await res.json();
            
            console.log('Datos recibidos de la API:', data); // Para debugging
            
            // Manejar consistentemente la respuesta
            let vehiculo;
            
            if (Array.isArray(data)) {
                // Si es un array (búsqueda por VIN), tomar el primer elemento
                if (data.length === 0) {
                    throw new Error("Vehículo no encontrado");
                }
                vehiculo = data[0];
            } else {
                // Si es un objeto (búsqueda por placa), usarlo directamente
                vehiculo = data;
            }
            
            if (!vehiculo) {
                throw new Error("Vehículo no encontrado");
            }

            // CORRECCIÓN: Verificar que el vehículo tenga los datos necesarios
            if (!vehiculo.id) {
                throw new Error("Datos del vehículo incompletos: falta ID");
            }

            // CORRECCIÓN: Validación del estado - usar valores exactos que esperas
            if (vehiculo.estado !== 'Activo') {
                // Mostrar el estado actual para debugging
                console.log('Estado del vehículo:', vehiculo.estado);
                throw new Error(`No se puede programar mantenimiento. El vehículo está ${vehiculo.estado || 'en estado desconocido'}.`);
            }
            
            console.log('Vehículo encontrado:', vehiculo); // Para debugging
            
            setVehiculoSeleccionado(vehiculo);
            
            // Actualizar el estado de nuevaAlerta con el ID del vehículo
            setNuevaAlerta(prev => ({ 
                ...prev, 
                vehiculo_id: vehiculo.id.toString()
            }));
            
            // LIMPIAR cualquier error previo
            setError(null);
            
        } catch (err) {
            console.error('Error en búsqueda de vehículo:', err);
            setError(err.message);
            setVehiculoSeleccionado(null);
            // Limpiar el ID del vehículo en caso de error
            setNuevaAlerta(prev => ({ ...prev, vehiculo_id: '' }));
        } finally {
            setIsLoadingBusqueda(false);
        }
    };

    // Resetear búsqueda al cerrar el modal
    const handleCloseModal = () => {
        setMostrarFormAlerta(false);
        setVehiculoSeleccionado(null);
        setPlacaBusqueda("");
        setVinBusqueda("");
        setTipoBusqueda("placa");
        setError(null);
        setPlacaError(false);
        setVinError(false);
    };

    // Handlers
    const handleNuevaAlertaChange = useCallback((e) => {
        const { name, value } = e.target;
        setNuevaAlerta(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

  const submitNuevaAlerta = useCallback(async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
        const payload = {
            vehiculo: parseInt(nuevaAlerta.vehiculo_id),
            tipo_mantenimiento: nuevaAlerta.tipo_mantenimiento,
            descripcion: nuevaAlerta.descripcion,
            kilometraje_programado: parseFloat(nuevaAlerta.kilometraje_programado),
            estado: nuevaAlerta.estado,
        };

        const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/alertas/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Error al crear la alerta');
        }

        const nuevaAlertaAPI = await response.json();

        const alertaTransformada = {
            ...nuevaAlertaAPI,
            vehiculo_placa: vehiculoSeleccionado?.placa || '',
            km_faltantes: nuevaAlertaAPI.km_faltantes,
            porcentaje_avance: nuevaAlertaAPI.porcentaje_avance
        };

        // Actualizar ambos estados
        setAlertas(prev => [...prev, alertaTransformada]);
        setAlertasProximas(prev => [...prev, alertaTransformada]);

        setNuevaAlerta(initialAlertaState);
        handleCloseModal();
    } catch (error) {
        console.error('Error al crear alerta:', error);
        setError(error.message || 'Ocurrió un error al crear la alerta');
    } finally {
        setIsLoading(false);
    }
}, [nuevaAlerta, setAlertas, initialAlertaState, vehiculoSeleccionado]);

    const abrirFormRegistro = useCallback((alerta) => {
        navigate(`/Flotas/RegistrarMantenimiento/${alerta.id}`);
    }, [navigate]);

    const abrirFormReprogramar = useCallback((alerta) => {
        setAlertaSeleccionada(alerta);
        setNuevoKmProgramado(alerta.kilometraje_programado || '');
        setMostrarFormReprogramar(true);
    }, []);

    const submitReprogramarAlerta = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `https://bei360.injupemp.gob.hn:8444/${alertaSeleccionada.id}/`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        kilometraje_programado: parseFloat(nuevoKmProgramado)
                    })
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Error al reprogramar la alerta');
            }

            const alertaActualizada = await response.json();

            setAlertas(prev => prev.map(alerta => 
                alerta.id === alertaSeleccionada.id 
                    ? { ...alerta, kilometraje_programado: alertaActualizada.kilometraje_programado }
                    : alerta
            ));

            setMostrarFormReprogramar(false);
        } catch (error) {
            console.error('Error al reprogramar alerta:', error);
            setError(error.message || 'Ocurrió un error al reprogramar la alerta');
        } finally {
            setIsLoading(false);
        }
    }, [alertaSeleccionada, nuevoKmProgramado, setAlertas]);

    const submitRegistroMantenimiento = useCallback(async (registroData, alerta) => {
        setIsLoading(true);
        setError(null);

        try {
            // 1. Registrar el mantenimiento en historial
            const mantenimientoResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/historial-mantenimiento/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    vehiculo: alerta.vehiculo_id || alerta.vehiculo,
                    descripcion: alerta.descripcion,
                    kilometraje: registroData.kilometrajeActual,
                    costo: registroData.costo,
                    observaciones: registroData.observaciones,
                    fecha_realizacion: registroData.fechaRealizacion
                })
            });

            if (!mantenimientoResponse.ok) {
                const errorData = await mantenimientoResponse.json();
                throw new Error(errorData.message || 'Error al registrar el mantenimiento');
            }

            // 2. Marcar alerta como realizada
            await fetch(`https://bei360.injupemp.gob.hn:8444/api/alertas/${alerta.id}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ realizada: true })
            });

            // 3. Subir evidencias si existen
            if (registroData.evidencia && registroData.evidencia.length > 0) {
                const uploadPromises = registroData.evidencia.map(async (archivo) => {
                    const formData = new FormData();
                    formData.append('archivo', archivo);
                    formData.append('alerta', alerta.id.toString());

                    await fetch('https://bei360.injupemp.gob.hn:8444/api/evidencias/', {
                        method: 'POST',
                        body: formData,
                    });
                });

                await Promise.all(uploadPromises);
            }

            // 4. Actualizar estado local
            setAlertas(prev => prev.filter(a => a.id !== alerta.id));
            setHistorial(prev => [...prev, mantenimientoResponse.json()]);

            navigate('/ProximosMantenimientos');
        } catch (error) {
            console.error('Error al registrar mantenimiento:', error);
            setError(error.message || 'Ocurrió un error al registrar el mantenimiento');
        } finally {
            setIsLoading(false);
        }
    }, [setAlertas, setHistorial, navigate]);

   // Calcular nivel de alerta con rangos fijos (5000 km)
    const getNivelAlerta = (kmFaltantes) => {
        if (kmFaltantes <= 0) return 'rojo';       
        if (kmFaltantes <= 999) return 'rojo';     
        if (kmFaltantes <= 1999) return 'amarillo'; 
        if (kmFaltantes <= 4999) return 'verde';   
        return 'azul';                             
    };

    const getColorAlerta = (nivel) => {
        switch (nivel) {
            case 'rojo': return '#f44336';    
            case 'amarillo': return '#ff9800';
            case 'verde': return '#4caf50';   
            case 'azul': return '#2196f3';    
            default: return '#9e9e9e';        
        }
    };

    const getTextoEstado = (kmFaltantes) => {
        if (kmFaltantes <= 0) return 'Vencido';
        if (kmFaltantes <= 999) return 'Urgente';
        if (kmFaltantes <= 1999) return 'Próximo';
        if (kmFaltantes <= 4999) return 'En rango';
        return 'Pendiente';
    };

    // Filtrar solo alertas pendientes
    const alertasPendientes = useMemo(() =>
        alertasProximas.filter(alerta => alerta.estado === 'pendiente'),
        [alertasProximas]);

    const paginatedAlertas = useMemo(() =>
        alertasPendientes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [alertasPendientes, page, rowsPerPage]);

    // Modal Forms
    const FormAlertaModal = useMemo(() => (
        <Modal open={mostrarFormAlerta} onClose={handleCloseModal}>
            <Box sx={modalStyle}>
                <Typography variant="h6" component="h3" gutterBottom>
                    Programar Nuevo Mantenimiento
                </Typography>
                {error && <Typography color="error" mb={2}>{error}</Typography>}
                
                <form onSubmit={submitNuevaAlerta}>
                    {/* Paso 1: Búsqueda por placa o VIN */}
                    {!vehiculoSeleccionado ? (
                        <>
                            <Typography variant="body1" mb={2}>
                                Seleccione el método de búsqueda e ingrese los datos del vehículo
                            </Typography>
                            
                            {/* Selector de tipo de búsqueda */}
                            <Box mb={3}>
                                <Typography variant="body2" color="textSecondary" mb={1}>
                                    Buscar por:
                                </Typography>
                                <ToggleButtonGroup
                                    value={tipoBusqueda}
                                    exclusive
                                    onChange={handleTipoBusquedaChange}
                                    fullWidth
                                >
                                    <ToggleButton value="placa">
                                        Placa
                                    </ToggleButton>
                                    <ToggleButton value="vin">
                                        Últimos 4 dígitos del VIN
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    {tipoBusqueda === "placa" ? (
                                        <TextField
                                            label="Placa del vehículo"
                                            value={placaBusqueda}
                                            onChange={(e) => setPlacaBusqueda(e.target.value)}
                                            fullWidth
                                            required
                                            error={placaError}
                                            helperText={placaError ? "Debe ingresar una placa" : ""}
                                            disabled={isLoadingBusqueda}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    buscarVehiculo();
                                                }
                                            }}
                                        />
                                    ) : (
                                        <TextField
                                            label="Últimos 4 dígitos del VIN"
                                            value={vinBusqueda}
                                            onChange={(e) => setVinBusqueda(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                            fullWidth
                                            required
                                            error={vinError}
                                            helperText={vinError ? "Debe ingresar exactamente 4 dígitos" : "Ingrese solo los últimos 4 dígitos del VIN"}
                                            disabled={isLoadingBusqueda}
                                            inputProps={{ maxLength: 4 }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    buscarVehiculo();
                                                }
                                            }}
                                        />
                                    )}
                                </Grid>
                                
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        onClick={buscarVehiculo}
                                        disabled={isLoadingBusqueda}
                                        fullWidth
                                        sx={{ py: 1.5, backgroundColor: '#19438C', color: 'white' }}
                                    >
                                        {isLoadingBusqueda ? <CircularProgress size={24} /> : "Buscar Vehículo"}
                                    </Button>
                                </Grid>
                            </Grid>
                            
                        </>
                    ) : (
                        /* Paso 2: Formulario de mantenimiento */
                        <>
                            {/* Mostrar datos del vehículo seleccionado */}
                            <Card sx={{ mb: 3 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Vehículo Seleccionado
                                    </Typography>
                                    
                                    <Divider sx={{ mb: 2 }} />
                                    
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">
                                                Placa:
                                            </Typography>
                                            <Typography variant="body1">
                                                {vehiculoSeleccionado.placa}
                                            </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">
                                                VIN:
                                            </Typography>
                                            <Typography variant="body1">
                                                {vehiculoSeleccionado.vin}
                                            </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">
                                                Marca:
                                            </Typography>
                                            <Typography variant="body1">
                                                {vehiculoSeleccionado.marca}
                                            </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">
                                                Modelo:
                                            </Typography>
                                            <Typography variant="body1">
                                                {vehiculoSeleccionado.modelo}
                                            </Typography>
                                        </Grid>
                                        
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">
                                                Año:
                                            </Typography>
                                            <Typography variant="body1">
                                                {vehiculoSeleccionado.año}
                                            </Typography>
                                        </Grid>
                                        
                                         <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">
                                                Color:
                                            </Typography>
                                            <Typography variant="body1">
                                                {vehiculoSeleccionado.color}
                                            </Typography>
                                        </Grid>

                                          <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">
                                                Kilometraje:
                                            </Typography>
                                            <Typography variant="body1">
                                                {vehiculoSeleccionado.kilometraje}
                                            </Typography>
                                        </Grid>

                                        {vehiculoSeleccionado.kilometraje_actual && (
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="textSecondary">
                                                    Kilometraje actual:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {vehiculoSeleccionado.kilometraje_actual} km
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>

                            <TextField
                                name="descripcion"
                                label="Descripción"
                                value={nuevaAlerta.descripcion}
                                onChange={handleNuevaAlertaChange}
                                fullWidth
                                margin="normal"
                                required
                                multiline
                                rows={2}
                            />

                            <TextField
                                name="kilometraje_programado"
                                label="Kilometraje programado"
                                type="number"
                                value={nuevaAlerta.kilometraje_programado}
                                onChange={handleNuevaAlertaChange}
                                fullWidth
                                margin="normal"
                                required
                                inputProps={{ min: 0 }}
                                helperText="Kilometraje en el que se debe realizar el mantenimiento"
                            />

                            <Box display="flex" justifyContent="space-between" mt={2}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setVehiculoSeleccionado(null);
                                        setPlacaBusqueda("");
                                        setVinBusqueda("");
                                    }}
                                    disabled={isLoading}
                                >
                                    Cambiar vehículo
                                </Button>
                                
                                <Box>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={handleCloseModal}
                                        sx={{ mr: 1 }}
                                        disabled={isLoading}
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        sx={{ backgroundColor: '#19438C', color: 'white' }}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Guardando...' : 'Guardar'}
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    )}
                </form>
            </Box>
        </Modal>
    ), [mostrarFormAlerta, nuevaAlerta, error, isLoading, placaBusqueda, vinBusqueda, vehiculoSeleccionado, isLoadingBusqueda, placaError, vinError, tipoBusqueda, submitNuevaAlerta, handleNuevaAlertaChange, handleTipoBusquedaChange]);

    const ReprogramarModal = useMemo(() => (
        <Dialog
            open={mostrarFormReprogramar}
            onClose={() => !isLoading && setMostrarFormReprogramar(false)}
        >
            <DialogTitle>Reprogramar Mantenimiento</DialogTitle>
            <DialogContent>
                {error && (
                    <Typography color="error" mb={2}>
                        {error}
                    </Typography>
                )}

                <Box mb={2}>
                    <TextField
                        label="Vehículo"
                        value={
                            alertaSeleccionada?.vehiculo_placa || 
                            alertaSeleccionada?.vehiculo_info ||
                            getNombreVehiculo(alertaSeleccionada?.vehiculo_id || alertaSeleccionada?.vehiculo) ||
                            'Vehículo no disponible'
                        }
                        fullWidth
                        InputProps={{ readOnly: true }}
                        variant="filled"
                        margin="normal"
                    />
                </Box>

                <TextField
                    label="Nuevo kilometraje programado"
                    type="number"
                    value={nuevoKmProgramado}
                    onChange={(e) => setNuevoKmProgramado(e.target.value)}
                    fullWidth
                    required
                    margin="normal"
                    inputProps={{ min: 0 }}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => setMostrarFormReprogramar(false)}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={submitReprogramarAlerta}
                    disabled={isLoading}
                    variant="contained"
                    sx={{ backgroundColor: '#19438C', color: 'white' }}
                >
                    {isLoading ? 'Guardando...' : 'Reprogramar'}
                </Button>
            </DialogActions>
        </Dialog>
    ), [mostrarFormReprogramar, isLoading, error, alertaSeleccionada, nuevoKmProgramado, submitReprogramarAlerta, getNombreVehiculo]);

    return (
        <>
            <Routes>
                <Route 
                    path="/registrar/:id" 
                    element={
                        <RegistrarMantenimiento
                            isLoading={isLoading}
                            error={error}
                            onSubmit={submitRegistroMantenimiento}
                            getNombreVehiculo={getNombreVehiculo}
                            alertas={alertasProximas}
                            vehiculos={vehiculos}
                        />
                    } 
                />
                <Route 
                    path="/" 
                    element={
                        <Container style={{ marginTop: 15 }}>
                            {/* Sección 1: Registrar alerta */}
                            <Box mb={4}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Button
                                        variant="contained"
                                        onClick={() => setMostrarFormAlerta(true)}
                                        startIcon={<Add />}
                                        sx={{ backgroundColor: '#19438C', color: 'white' }}
                                    >
                                        Programar Mantenimiento
                                    </Button>
                                </Box>

                                {FormAlertaModal}
                            </Box>

                            {/* Sección 2: Listar alertas próximas */}
                            <Box mb={4}>
                                <Typography variant="h5" gutterBottom>
                                    Mantenimientos Programados
                                </Typography>
                                
                                {isLoadingAlertas ? (
                                    <Box display="flex" justifyContent="center" my={4}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <>
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <StyledTableRow>
                                                        <StyledTableCell>Placa</StyledTableCell>
                                                        <StyledTableCell>Tipo</StyledTableCell>
                                                        <StyledTableCell>Descripción</StyledTableCell>
                                                        <StyledTableCell>Kilometraje Programado</StyledTableCell>
                                                        <StyledTableCell>Progreso</StyledTableCell>
                                                        <StyledTableCell>Estado</StyledTableCell>
                                                        <StyledTableCell>Acciones</StyledTableCell>
                                                    </StyledTableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedAlertas.map(alerta => {
                                                        const kmLimite = parseFloat(alerta.kilometraje_programado);
                                                        const nivelAlerta = getNivelAlerta(alerta.km_faltantes, kmLimite);
                                                        const textoEstado = getTextoEstado(alerta.km_faltantes, kmLimite);

                                                        return (
                                                            <StyledTableRow key={alerta.id} hover>
                                                                <StyledTableCell>
                                                                    {alerta.vehiculo_placa}
                                                                </StyledTableCell>
                                                                <StyledTableCell sx={{ textTransform: 'capitalize' }}>
                                                                    {alerta.tipo_mantenimiento?.replace('_', ' ')}
                                                                </StyledTableCell>
                                                                <StyledTableCell>{alerta.descripcion}</StyledTableCell>
                                                                <StyledTableCell>
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                        <Typography variant="body2">
                                                                            {parseFloat(alerta.kilometraje_programado).toLocaleString()} km
                                                                        </Typography>
                                                                        <Chip
                                                                            label={`Faltan ${Math.abs(alerta.km_faltantes).toLocaleString()} km`}
                                                                            size="small"
                                                                            sx={{
                                                                                backgroundColor: getColorAlerta(nivelAlerta),
                                                                                color: 'white',
                                                                                fontSize: '0.7rem'
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                </StyledTableCell>
                                                                <StyledTableCell>
                                                                    <Box display="flex" alignItems="center" gap={1}>
                                                                        <LinearProgress 
                                                                            variant="determinate" 
                                                                            value={alerta.porcentaje_avance} 
                                                                            sx={{ 
                                                                                width: '100%',
                                                                                height: 8,
                                                                                borderRadius: 4,
                                                                                backgroundColor: '#e0e0e0',
                                                                                '& .MuiLinearProgress-bar': {
                                                                                    backgroundColor: getColorAlerta(nivelAlerta)
                                                                                }
                                                                            }} 
                                                                        />
                                                                        <Typography variant="body2" sx={{ minWidth: 40 }}>
                                                                            {alerta.porcentaje_avance}%
                                                                        </Typography>
                                                                    </Box>
                                                                </StyledTableCell>
                                                                <StyledTableCell>
                                                                    <Chip
                                                                        label={textoEstado}
                                                                        sx={{
                                                                            backgroundColor: getColorAlerta(nivelAlerta),
                                                                            color: 'white'
                                                                        }}
                                                                        size="small"
                                                                    />
                                                                </StyledTableCell>
                                                                <StyledTableCell>
                                                                    <Box display="flex" gap={1}>
                                                                        <Tooltip title="Reprogramar">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => abrirFormReprogramar(alerta)}
                                                                                disabled={isLoading}
                                                                            >
                                                                                <UpdateIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Registrar mantenimiento">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => abrirFormRegistro(alerta)}
                                                                                disabled={isLoading}
                                                                                color={nivelAlerta === 'rojo' ? 'error' : 'primary'}
                                                                            >
                                                                                <AddCircleIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                </StyledTableCell>
                                                            </StyledTableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <TablePagination
                                            rowsPerPageOptions={[5, 10, 25]}
                                            component="div"
                                            count={alertasPendientes.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            labelRowsPerPage="Filas por página:"
                                            labelDisplayedRows={({ from, to, count }) => 
                                                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                                            }
                                        />
                                    </>
                                )}
                            </Box>

                            {ReprogramarModal}
                        </Container>
                    } 
                />
            </Routes>
        </>
    );
};

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: '80vh',
    overflowY: 'auto'
};

export default AlertasMantenimiento;